import React, { useEffect, useRef } from "react";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { motion, useAnimation, useInView, Variants } from "framer-motion";

const fadeUpVariant: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ScrollFadeBlock: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.2 });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      variants={fadeUpVariant}
      initial="hidden"
      animate={controls}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const BenefitsSection: React.FC = () => {
  return (
    <section id="beneficios" className="section-hyper scroll-snap-section">
      <div className="section-content-hyper">
        <ScrollFadeBlock className="section-title-hyper">
          Beneficios para vos
          <h3 className="section-title-hyper">¿Queres empezar a invertir sin tener una fortuna?</h3>
        </ScrollFadeBlock>

        <ScrollFadeBlock className="intro-text">
          <p>
            Tu auto puede ser tu primer paso. <br />
            Sumate a Arrank! y hacelo generar ingresos todos los meses. <br />
            "Nosotros nos encargamos,  Vos hace lo tuyo".
          </p>
        </ScrollFadeBlock>

        <ScrollFadeBlock className="benefits-grid">
          <ScrollFadeBlock className="benefit-card primary-benefit">
            <div className="benefit-icon"><i className="bi bi-currency-dollar"></i></div>
            <h4 className="benefit-title">Ingresos mensuales asegurados</h4>
            <p className="benefit-description">
              Recibí pagos puntuales y transparentes con reportes detallados. 
              Sin sorpresas, sin complicaciones.
            </p>
          </ScrollFadeBlock>

          <ScrollFadeBlock className="benefit-card primary-benefit">
            <div className="benefit-icon"><i className="bi bi-shield-lock"></i></div>
            <h4 className="benefit-title">Seguridad integral</h4>
            <p className="benefit-description">
              Cobertura completa con seguro para transporte de pasajeros, 
              rastreo satelital GPS y control de velocidad.
            </p>
          </ScrollFadeBlock>

          <ScrollFadeBlock className="benefit-card primary-benefit">
            <div className="benefit-icon"><i className="bi bi-tools"></i></div>
            <h4 className="benefit-title">Mantenimiento incluido</h4>
            <p className="benefit-description">
              Service programado, limpieza semanal y chequeos regulares. 
              Tu auto siempre en perfectas condiciones.
            </p>
          </ScrollFadeBlock>

          <ScrollFadeBlock className="benefit-card primary-benefit">
            <div className="benefit-icon"><i className="bi bi-phone"></i></div>
            <h4 className="benefit-title">Gestión 100% digital</h4>
            <p className="benefit-description">
              Seguí el estado de tu auto en tiempo real, reportes automáticos 
              y soporte 24/7 desde tu celular.
            </p>
          </ScrollFadeBlock>

          <ScrollFadeBlock className="benefit-card primary-benefit">
            <div className="benefit-icon"><i className="bi bi-clock"></i></div>
            <h4 className="benefit-title">Flexibilidad total</h4>
            <p className="benefit-description">
              Decidí cuándo querés que tu auto trabaje. Podés pausar el servicio 
              cuando necesites usarlo sin penalidades.
            </p>
          </ScrollFadeBlock>

          <ScrollFadeBlock className="benefit-card primary-benefit">
            <div className="benefit-icon"><i className="bi bi-person-fill"></i></div>
            <h4 className="benefit-title">Red de profesionales</h4>
            <p className="benefit-description">
              Conductores pre-seleccionados con experiencia comprobada, 
              capacitación constante y evaluaciones periódicas.
            </p>
          </ScrollFadeBlock>
        </ScrollFadeBlock>

        <ScrollFadeBlock className="additional-guarantees">
          <h3 className="guarantees-title">Además te garantizamos:</h3>
          <div className="guarantees-list">
            <ScrollFadeBlock className="guarantee-item">
                <span className="guarantee-icon"><i className="bi bi-arrow-right-square-fill"></i></span>
              <span>Conductores verificados con antecedentes chequeados</span>
            </ScrollFadeBlock>
            <ScrollFadeBlock className="guarantee-item">
              <span className="guarantee-icon"><i className="bi bi-arrow-right-square-fill"></i></span>
              <span>Kilometraje limitado para cuidar tu inversión</span>
            </ScrollFadeBlock>
            <ScrollFadeBlock className="guarantee-item">
              <span className="guarantee-icon"><i className="bi bi-arrow-right-square-fill"></i></span>
              <span>Alta de monotributo y soporte contable incluido</span>
            </ScrollFadeBlock>
            <ScrollFadeBlock className="guarantee-item">
              <span className="guarantee-icon"><i className="bi bi-arrow-right-square-fill"></i></span>
              <span>Proceso de selección riguroso y capacitación continua</span>
            </ScrollFadeBlock>
          </div>
      {/* Comparativa fuera de la caja de garantías */}
      <div className="comparison-card d-flex justify-content-center align-items-center" style={{ background: '#fff', borderRadius: '32px', boxShadow: '0 8px 32px rgba(16,185,129,0.10)', padding: '2.5rem 2rem', maxWidth: '900px', margin: '2.5rem auto 0 auto', flexWrap: 'nowrap', gap: '2rem' }}>
        <div className="comparison-side" style={{ flex: '1 1 340px', background: 'linear-gradient(135deg, #e0f7fa 0%, #d1fae5 100%)', borderRadius: '24px', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 2px 12px rgba(16,185,129,0.10)' }}>
          <i className="bi bi-car-front" style={{ fontSize: '2.8rem', color: '#10b981', marginBottom: '1rem' }}></i>
          <h5 style={{ color: '#052c4d', fontWeight: 700, marginBottom: '1.2rem' }}>Auto</h5>
          <div className="w-100 mb-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.2rem' }}>Inversión</span>
              <span className="badge bg-info text-dark" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>$13.000 USD</span>
            </div>
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.2rem' }}>Rentabilidad</span>
              <span className="badge bg-success" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>Alta</span>
            </div>
          </div>
          <div className="w-100 mb-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.2rem' }}>Mantenimiento</span>
              <span className="badge bg-warning text-dark" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>Medio</span>
            </div>
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.2rem' }}>Rentabilidad anual</span>
              <span className="badge bg-primary" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>16% anual</span>
            </div>
          </div>
        </div>
        <div className="comparison-vs d-flex flex-column align-items-center justify-content-center" style={{ flex: '0 0 80px', fontWeight: 700, fontSize: '2.2rem', color: '#052c4d' }}>
          VS
        </div>
        <div className="comparison-side" style={{ flex: '1 1 340px', background: 'linear-gradient(135deg, #f3e5f5 0%, #e0e7ff 100%)', borderRadius: '24px', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 2px 12px rgba(5,44,77,0.10)' }}>
          <i className="bi bi-building" style={{ fontSize: '2.8rem', color: '#052c4d', marginBottom: '1rem' }}></i>
          <h5 style={{ color: '#052c4d', fontWeight: 700, marginBottom: '1.2rem' }}>Departamento</h5>
          <div className="w-100 mb-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#111', marginBottom: '0.2rem' }}>Inversión</span>
              <span className="badge bg-info text-dark" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>$85.000 USD</span>
            </div>
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#111', marginBottom: '0.2rem' }}>Rentabilidad</span>
              <span className="badge bg-danger" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>Baja</span>
            </div>
          </div>
          <div className="w-100 mb-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#111', marginBottom: '0.2rem' }}>Mantenimiento</span>
              <span className="badge bg-success" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>Bajo</span>
            </div>
            <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
              <span style={{ fontSize: '0.85rem', color: '#111', marginBottom: '0.2rem' }}>Rentabilidad anual</span>
              <span className="badge bg-secondary" style={{ fontSize: '1rem', padding: '0.7rem 1.2rem', borderRadius: '16px' }}>4.5% anual</span>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col text-center" style={{ color: '#052c4d', fontWeight: 500, fontSize: '1.1rem' }}>
          Con Arrank! invertís menos y ganás más cada mes, sin complicaciones ni gestiones difíciles.
        </div>
      </div>
        </ScrollFadeBlock>

      </div>
    </section>
  );
};

export default BenefitsSection;
